import { useId, useRef, useState, type FormEvent } from "react";
import {
  Home,
  LayoutGrid,
  MapPin,
  Menu,
  MessageCircle,
  PackageSearch,
  Search,
  ShoppingBasket,
  Store,
  UserRound,
  X,
} from "lucide-react";
import {
  Link,
  NavLink,
  Outlet,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { useCart } from "../context/CartContext";
import { useStore } from "../context/StoreContext";
import { apiRequest, queryString } from "../lib/api";
import { formatRupiah } from "../lib/format";
import { normalizeProductCollection, type Branch } from "../lib/types";
import { branchWhatsAppUrl } from "../lib/whatsapp";
import { Button, ErrorState, Modal, Notice, Skeleton } from "./ui";

function BranchSwitcher({ onNotice }: { onNotice: (value: string) => void }) {
  const { store, activeBranchId, selectBranch } = useStore();
  const cart = useCart();
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState<Branch>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  const changeBranch = async (branch: Branch) => {
    if (String(branch.id) === String(activeBranchId)) {
      setOpen(false);
      return;
    }
    if (cart.items.length > 0 && !pending) {
      setPending(branch);
      return;
    }
    setBusy(true);
    setError(undefined);
    try {
      let message = `Cabang aktif diubah ke ${branch.name}.`;
      if (cart.items.length > 0) {
        const payload = await apiRequest<unknown>(
          `/api/public/products${queryString({ branch_id: String(branch.id), limit: 48, include_total: 0 })}`,
        );
        const result = cart.reconcileBranch(
          branch.id,
          normalizeProductCollection(payload).items,
        );
        if (result.removed.length || result.updated.length) {
          const parts = [
            result.updated.length
              ? `${result.updated.length} produk diperbarui`
              : "",
            result.removed.length
              ? `${result.removed.length} produk tidak tersedia di cabang baru`
              : "",
          ].filter(Boolean);
          message = `${branch.name} dipilih. ${parts.join(" dan ")}.`;
        }
      }
      await selectBranch(branch.id);
      onNotice(message);
      setOpen(false);
      setPending(undefined);
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : "Cabang belum dapat diganti.",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        className="branch-button"
        type="button"
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
      >
        <MapPin aria-hidden="true" size={21} />
        <span>
          <small>Belanja dari</small>
          <strong>{store?.activeBranch?.name ?? "Memuat cabang…"}</strong>
        </span>
        <span className="branch-button__action">
          Ganti ({store?.branches.length ?? 0})
        </span>
      </button>
      <Modal
        open={open}
        onClose={() => {
          setOpen(false);
          setPending(undefined);
          setError(undefined);
        }}
        title={
          pending
            ? "Pindah cabang?"
            : `Pilih Cabang (${store?.branches.length ?? 0} tersedia)`
        }
        description={
          pending
            ? `Keranjang Anda berasal dari ${store?.activeBranch?.name}. Isinya akan diperiksa kembali.`
            : "Harga, stok, pengantaran, dan WhatsApp mengikuti cabang aktif."
        }
      >
        {error && <Notice tone="danger">{error}</Notice>}
        {pending ? (
          <div className="confirm-stack">
            <div className="confirm-branch">
              <MapPin aria-hidden="true" />
              <div>
                <strong>{pending.name}</strong>
                <span>{pending.address}</span>
              </div>
            </div>
            <p>
              Produk yang tidak tersedia akan dihapus. Harga dan jumlah akan
              diperbarui sesuai stok cabang baru.
            </p>
            <div className="modal-actions">
              <Button
                variant="secondary"
                onClick={() => setPending(undefined)}
                disabled={busy}
              >
                Batal
              </Button>
              <Button onClick={() => void changeBranch(pending)} busy={busy}>
                Pindah Cabang
              </Button>
            </div>
          </div>
        ) : (
          <div className="branch-list">
            {store?.branches
              .filter((branch) => branch.status !== "inactive")
              .map((branch) => (
                <button
                  className={`branch-option ${String(branch.id) === String(activeBranchId) ? "branch-option--active" : ""}`}
                  type="button"
                  key={String(branch.id)}
                  onClick={() => void changeBranch(branch)}
                  disabled={busy}
                >
                  <span className="branch-option__icon">
                    <Store aria-hidden="true" />
                  </span>
                  <span>
                    <strong>{branch.name}</strong>
                    <small>{branch.address}</small>
                    <small>
                      Buka {branch.openingTime}–{branch.closingTime}
                    </small>
                  </span>
                  <span>
                    {String(branch.id) === String(activeBranchId)
                      ? "Sedang Aktif"
                      : "Pilih Cabang"}
                  </span>
                </button>
              ))}
          </div>
        )}
      </Modal>
    </>
  );
}

function SearchBox() {
  const inputId = useId();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [query, setQuery] = useState(params.get("q") ?? "");
  const submit = (event: FormEvent) => {
    event.preventDefault();
    void navigate(
      query.trim() ? `/?q=${encodeURIComponent(query.trim())}` : "/",
    );
  };
  return (
    <form className="search-box" role="search" onSubmit={submit}>
      <Search aria-hidden="true" size={21} />
      <label className="sr-only" htmlFor={inputId}>
        Cari produk
      </label>
      <input
        id={inputId}
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder="Cari bayam, beras, bumbu…"
      />
      {query && (
        <button
          type="button"
          onClick={() => setQuery("")}
          aria-label="Hapus pencarian"
        >
          <X aria-hidden="true" size={18} />
        </button>
      )}
      <button
        className="search-box__submit"
        type="submit"
        aria-label="Cari produk"
        title="Cari"
      >
        <Search aria-hidden="true" size={18} />
      </button>
    </form>
  );
}

export function StorefrontLayout() {
  const { store, loading, error, reload } = useStore();
  const cart = useCart();
  const [mobileMenu, setMobileMenu] = useState(false);
  const mobileMenuButtonRef = useRef<HTMLButtonElement>(null);
  const mobileMenuRef = useRef<HTMLElement>(null);
  const [notice, setNotice] = useState<string>();
  const whatsappUrl = branchWhatsAppUrl(
    store?.activeBranch?.whatsappNumber,
    store?.activeBranch?.name,
  );
  const menuCategories = (store?.navigationCategories ?? store?.categories ?? [])
    .filter((category) => category.navigationOrder !== undefined)
    .sort(
      (left, right) =>
        (left.navigationOrder ?? 99) - (right.navigationOrder ?? 99),
    )
    .slice(0, 8);

  if (loading && !store)
    return (
      <main id="main-content" className="page-shell">
        <Skeleton rows={6} cards />
      </main>
    );
  if (error && !store)
    return (
      <main id="main-content" className="page-shell">
        <ErrorState message={error} onRetry={() => void reload()} />
      </main>
    );

  return (
    <div
      className="storefront"
      onPointerDownCapture={(event) => {
        if (!mobileMenu || !(event.target instanceof Node)) return;
        if (
          mobileMenuButtonRef.current?.contains(event.target) ||
          mobileMenuRef.current?.contains(event.target)
        ) {
          return;
        }
        setMobileMenu(false);
      }}
      onKeyDownCapture={(event) => {
        if (event.key !== "Escape" || !mobileMenu) return;
        setMobileMenu(false);
        mobileMenuButtonRef.current?.focus();
      }}
    >
      <header className="site-header">
        <div className="header-main page-shell">
          <Link
            className="brand brand--image"
            to="/"
            aria-label={`${store?.storeName ?? "Warung Sedap Sayur"}, beranda`}
          >
            <img
              className="brand__logo"
              src="/brand/warung-sedap-sayur-logo.webp"
              width={1190}
              height={433}
              fetchPriority="high"
              alt=""
              aria-hidden="true"
            />
          </Link>
          <div className="header-branch">
            <BranchSwitcher onNotice={setNotice} />
          </div>
          <div className="header-search">
            <SearchBox />
          </div>
          <div className="header-actions">
            <Link className="header-action" to="/member">
              <UserRound aria-hidden="true" />
              <span>Member</span>
            </Link>
            {whatsappUrl && (
              <a
                className="header-action"
                href={whatsappUrl}
                target="_blank"
                rel="noreferrer"
              >
                <MessageCircle aria-hidden="true" />
                <span>WhatsApp</span>
              </a>
            )}
            <Link className="header-action header-action--cart" to="/keranjang">
              <ShoppingBasket aria-hidden="true" />
              <span>Keranjang</span>
              {cart.itemCount > 0 && (
                <b aria-label={`${cart.itemCount} barang`}>{cart.itemCount}</b>
              )}
            </Link>
            <button
              ref={mobileMenuButtonRef}
              className="mobile-menu-button"
              type="button"
              onClick={() => setMobileMenu((value) => !value)}
              aria-expanded={mobileMenu}
              aria-controls="mobile-menu"
              aria-label={mobileMenu ? "Tutup menu" : "Buka menu"}
            >
              {mobileMenu ? (
                <X aria-hidden="true" />
              ) : (
                <Menu aria-hidden="true" />
              )}
            </button>
          </div>
        </div>
        <div className="mobile-branch page-shell">
          <BranchSwitcher onNotice={setNotice} />
        </div>
        <div className="mobile-search page-shell">
          <SearchBox />
        </div>
        <nav
          ref={mobileMenuRef}
          id="mobile-menu"
          className={`category-nav ${mobileMenu ? "category-nav--open" : ""}`}
          aria-label="Kategori utama"
        >
          <div className="page-shell category-nav__inner">
            <Link to="/" onClick={() => setMobileMenu(false)}>
              Beranda
            </Link>
            {menuCategories.map((category) => (
              <Link
                to={`/kategori/${category.slug}`}
                key={String(category.id)}
                onClick={() => setMobileMenu(false)}
              >
                {category.name}
              </Link>
            ))}
            <Link to="/pesanan" onClick={() => setMobileMenu(false)}>
              Cek Pesanan
            </Link>
            <Link to="/member" onClick={() => setMobileMenu(false)}>
              Member
            </Link>
          </div>
        </nav>
      </header>

      {notice && (
        <div className="page-shell notice-wrap">
          <Notice>{notice}</Notice>
        </div>
      )}
      {store?.degraded && (
        <div className="page-shell notice-wrap">
          <Notice tone="warning">
            Katalog sedang diperbarui. Informasi produk dan stok sementara
            belum tersedia; silakan coba lagi beberapa saat.
          </Notice>
        </div>
      )}
      {store?.activeBranch?.status === "temporarily_closed" && (
        <div className="page-shell notice-wrap">
          <Notice tone="warning">
            Cabang sedang tutup sementara. Anda tetap dapat melihat produk,
            tetapi pesanan baru belum dapat dibuat.
          </Notice>
        </div>
      )}

      <main id="main-content">
        <Outlet />
      </main>

      <footer className="site-footer">
        <div className="page-shell footer-grid">
          <div>
            <img
              className="footer-logo"
              src="/brand/warung-sedap-sayur-logo.webp"
              width={1190}
              height={433}
              loading="lazy"
              alt={store?.storeName ?? "Warung Sedap Sayur"}
            />
            <p>
              {store?.storeTagline ??
                "Segar untuk keluarga, mudah dari cabang terdekat."}
            </p>
          </div>
          <div>
            <h3>Cabang aktif</h3>
            <p>
              <strong>{store?.activeBranch?.name}</strong>
              <br />
              {store?.activeBranch?.address}
              <br />
              Buka {store?.activeBranch?.openingTime}–
              {store?.activeBranch?.closingTime}
            </p>
          </div>
          <div>
            <h3>Bantuan</h3>
            <Link to="/pesanan">Cek Pesanan</Link>
            <Link to="/hubungi">Hubungi Kami</Link>
            <Link to="/kebijakan-privasi">Kebijakan Privasi</Link>
            <Link to="/syarat-ketentuan">Syarat & Ketentuan</Link>
          </div>
        </div>
        <div className="page-shell footer-bottom">
          © 2026 {store?.storeName ?? "Warung Sedap Sayur"}. Harga dan stok
          mengikuti cabang aktif.
        </div>
      </footer>

      <div className="storefront-floating-actions">
        {cart.itemCount > 0 && (
          <Link className="mobile-cart-bar" to="/keranjang">
            <span>
              <ShoppingBasket aria-hidden="true" />
              <strong>{cart.itemCount} barang</strong>
              <small>{formatRupiah(cart.subtotal)}</small>
            </span>
            <b>Lihat Keranjang</b>
          </Link>
        )}

        {whatsappUrl && (
          <a
            className={`live-chat-pill ${cart.itemCount > 0 ? "live-chat-pill--with-cart" : ""}`}
            href={whatsappUrl}
            target="_blank"
            rel="noreferrer"
            aria-label="Live chat WhatsApp"
          >
            <span className="live-chat-pill__dot" aria-hidden="true" />
            <small>LIVE</small>
            <MessageCircle aria-hidden="true" />
            <strong>Tanya</strong>
          </a>
        )}
      </div>

      <nav className="bottom-nav" aria-label="Navigasi utama">
        <NavLink to="/" end>
          <Home aria-hidden="true" />
          <span>Beranda</span>
        </NavLink>
        <NavLink
          to={
            menuCategories[0] ? `/kategori/${menuCategories[0].slug}` : "/"
          }
        >
          <LayoutGrid aria-hidden="true" />
          <span>Kategori</span>
        </NavLink>
        <NavLink to="/keranjang">
          <ShoppingBasket aria-hidden="true" />
          <span>Keranjang</span>
          {cart.itemCount > 0 && <b>{cart.itemCount}</b>}
        </NavLink>
        <NavLink to="/pesanan">
          <PackageSearch aria-hidden="true" />
          <span>Pesanan</span>
        </NavLink>
      </nav>
    </div>
  );
}
